import { create } from 'zustand';
import ServiceHorarios from '@/services/ServiceHorarios';
import { notify } from '@/stores/useToast';
import type { FormRecomendacao } from '@/lib/jornadaDetalhe';

export interface DetalheAnalise {
  [k: string]: unknown;
  analiseId: number;
  step?: string;
  risco?: string | null;
}
export interface OptionItem {
  code: number;
  description: string | null;
}
// `diagnostic` bate com o DTO do BFF (HjRiskConfig._to_dto); evita drift futuro.
export interface OcorrenciaCatalogo {
  id: number;
  occurrence: string;
  diagnostic?: string | null;
  risk?: string | null;
}

interface State {
  detalhe: DetalheAnalise | null;
  optWorkRegime: OptionItem[];
  optJourneyType: OptionItem[];
  optPartTime: OptionItem[];
  ocorrencias: OcorrenciaCatalogo[];
  carregando: boolean;
  salvando: boolean;
  carregarDetalhe: (analiseId: number) => Promise<void>;
  carregarOptions: () => Promise<void>;
  carregarOcorrencias: () => Promise<void>;
  salvar: (
    analiseId: number,
    payload: ReturnType<typeof import('@/lib/jornadaDetalhe').toUpdatePayload>,
  ) => Promise<void>;
  eventos: Record<string, unknown>[];
  eventosTotal: number;
  eventosFiltros: { versions: string[]; events: { code: string; description: string | null }[] };
  eventosPage: number;
  eventosFiltro: Record<string, unknown>;
  carregarEventos: (trabalhadorId: number) => Promise<void>;
  carregarFiltrosEventos: (trabalhadorId: number) => Promise<void>;
  exportarEventos: (trabalhadorId: number) => Promise<void>;
  setEventosPage: (trabalhadorId: number, p: number) => Promise<void>;
  setEventosFiltro: (trabalhadorId: number, f: Record<string, unknown>) => Promise<void>;
}

export const useJornadaDetalheStore = create<State>((set) => ({
  detalhe: null,
  optWorkRegime: [],
  optJourneyType: [],
  optPartTime: [],
  ocorrencias: [],
  carregando: false,
  salvando: false,
  eventos: [],
  eventosTotal: 0,
  eventosFiltros: { versions: [], events: [] },
  eventosPage: 0,
  eventosFiltro: {},

  async carregarDetalhe(analiseId) {
    set({ carregando: true });
    try {
      const { data } = await ServiceHorarios.getAnaliseDetalhe(analiseId);
      set({ detalhe: data });
    } catch {
      notify.error('Erro ao carregar a análise.');
    } finally {
      set({ carregando: false });
    }
  },

  async carregarOptions() {
    try {
      const [wr, jt, pt] = await Promise.all([
        ServiceHorarios.optionsWorkRegime(),
        ServiceHorarios.optionsJourneyType(),
        ServiceHorarios.optionsPartTime(),
      ]);
      set({
        optWorkRegime: wr.data ?? [],
        optJourneyType: jt.data ?? [],
        optPartTime: pt.data ?? [],
      });
    } catch {
      notify.error('Erro ao carregar as opções.');
    }
  },

  async carregarOcorrencias() {
    try {
      const { data } = await ServiceHorarios.listarOcorrenciasCatalogo();
      set({ ocorrencias: (data?.content ?? data ?? []) as OcorrenciaCatalogo[] });
    } catch {
      notify.error('Erro ao carregar as ocorrências.');
    }
  },

  async salvar(analiseId, payload) {
    set({ salvando: true });
    try {
      await ServiceHorarios.salvarAnalise(analiseId, payload);
      const { data } = await ServiceHorarios.getAnaliseDetalhe(analiseId);
      set({ detalhe: data });
      notify.success('Análise salva.');
    } catch {
      notify.error('Erro ao salvar a análise.');
    } finally {
      set({ salvando: false });
    }
  },

  async carregarEventos(trabalhadorId) {
    try {
      const s = useJornadaDetalheStore.getState();
      const { data } = await ServiceHorarios.listarEventos(trabalhadorId, s.eventosFiltro, {
        page: s.eventosPage,
        size: 50,
      });
      set({ eventos: data.content ?? [], eventosTotal: data.totalElements ?? 0 });
    } catch {
      notify.error('Erro ao carregar as movimentações.');
    }
  },
  async carregarFiltrosEventos(trabalhadorId) {
    try {
      const { data } = await ServiceHorarios.filtrosEventos(trabalhadorId);
      set({ eventosFiltros: { versions: data.versions ?? [], events: data.events ?? [] } });
    } catch {
      notify.error('Erro ao carregar os filtros.');
    }
  },
  async exportarEventos(trabalhadorId) {
    try {
      await ServiceHorarios.exportarEventos(
        trabalhadorId,
        useJornadaDetalheStore.getState().eventosFiltro,
      );
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error('Erro ao exportar as movimentações.');
    }
  },
  async setEventosPage(trabalhadorId, p) {
    set({ eventosPage: p });
    await useJornadaDetalheStore.getState().carregarEventos(trabalhadorId);
  },
  async setEventosFiltro(trabalhadorId, f) {
    set({ eventosFiltro: f, eventosPage: 0 });
    await useJornadaDetalheStore.getState().carregarEventos(trabalhadorId);
  },
}));

export type { FormRecomendacao };
